<script lang="ts">
  import SpinWheel from './components/SpinWheel.svelte';
  import Header from './components/Header.svelte';
  import Footer from './components/Footer.svelte';
  import AdSenseBanner from './components/AdSenseBanner.svelte';
  import LeadCapture from './components/LeadCapture.svelte';
  import BacklinksSection from './components/BacklinksSection.svelte';
  import { generateInterestingFact, type AIResponse } from './lib/aiAgent';
  
  let showLeadModal = $state(false);
  let hasSpunToday = $state(false);
  let lastWin = $state<string | null>(null);
  let interestingFact = $state<AIResponse | null>(null);
  let isLoadingFact = $state(false);
  
  async function loadInterestingFact() {
    isLoadingFact = true;
    interestingFact = await generateInterestingFact();
    isLoadingFact = false;
  }
  
  $effect(() => {
    loadInterestingFact();
  });
  
  function handleWin(prize: string) {
    lastWin = prize;
    showLeadModal = true;
    hasSpunToday = true;
    
    localStorage.setItem('lastSpinDate', new Date().toDateString());
    localStorage.setItem('lastWin', prize);
  }
  
  $effect(() => {
    const lastSpinDate = localStorage.getItem('lastSpinDate');
    const today = new Date().toDateString();
    
    if (lastSpinDate === today) {
      hasSpunToday = true;
      lastWin = localStorage.getItem('lastWin');
    }
  });
  
  function scrollToWheel() {
    document.getElementById('wheel-section')?.scrollIntoView({ behavior: 'smooth' });
  }
</script>

<main class="app-container">
  <Header />
  
  <section class="hero-section">
    <div class="hero-content">
      <h1 class="hero-title">
        🎰 <span class="gradient-text">Spin & Win Amazing Prizes!</span> 🎁
      </h1>
      <p class="hero-subtitle">
        Join <strong>15,420+ winners</strong> who claimed their daily rewards! 
        Free spins every day - No purchase necessary!
      </p>
      
      {#if !hasSpunToday}
        <div class="cta-box">
          <p class="urgency-text">🔥 Limited spins available today!</p>
          <button class="spin-now-btn" onclick={scrollToWheel}>
            ⚡ SPIN NOW - IT'S FREE!
          </button>
          <p class="guarantee-text">✓ 100% Free ✓ Instant Win ✓ No Registration Required</p>
        </div>
      {:else}
        <div class="already-spun-box">
          <p class="success-message">🎉 You already spun today! Come back tomorrow for another chance!</p>
          {#if lastWin}
            <p class="last-win">Your prize: <strong>{lastWin}</strong></p>
          {/if}
        </div>
      {/if}
    </div>
  </section>
  
  <AdSenseBanner position="top" />
  
  <section id="wheel-section" class="wheel-section">
    <div class="wheel-container">
      <SpinWheel onWin={(e) => handleWin(e.detail)} disabled={hasSpunToday} />
    </div>
    
    <div class="social-proof">
      <div class="winner-ticker">
        <span>🏆 Recent Winners:</span>
        <span class="ticker-content">
          Sarah won $500 • Mike won iPhone 15 • Emma won $1000 • John won PS5 • Lisa won $250 • 
        </span>
      </div>
    </div>
  </section>
  
  <AdSenseBanner position="middle" />
  
  {#if showLeadModal}
    <LeadCapture prize={lastWin} onClose={() => showLeadModal = false} />
  {/if}
  
  <section class="features-section" id="features">
    <h2>Why Choose SpinWheel Pro?</h2>
    <div class="features-grid">
      <div class="feature-card">
        <div class="feature-icon">🎯</div>
        <h3>Daily Free Spins</h3>
        <p>Get a free spin every 24 hours! No credit card required, completely free to play.</p>
      </div>
      <div class="feature-card">
        <div class="feature-icon">💰</div>
        <h3>Real Cash Prizes</h3>
        <p>Win real money, gift cards, electronics, and exclusive rewards instantly!</p>
      </div>
      <div class="feature-card">
        <div class="feature-icon">🔒</div>
        <h3>100% Secure & Fair</h3>
        <p>Our wheel uses certified random algorithms. Every spin is fair and transparent.</p>
      </div>
      <div class="feature-card">
        <div class="feature-icon">⚡</div>
        <h3>Instant Payouts</h3>
        <p>Claim your winnings immediately. Fast processing and quick withdrawals.</p>
      </div>
    </div>
  </section>
  
  <section class="how-it-works" id="how-it-works">
    <h2>How to Win in 3 Easy Steps</h2>
    <div class="steps-container">
      <div class="step">
        <div class="step-number">1</div>
        <h3>Click Spin</h3>
        <p>Hit the big red button to start the wheel spinning!</p>
      </div>
      <div class="step">
        <div class="step-number">2</div>
        <h3>Wait for Result</h3>
        <p>Watch as the wheel slows down to reveal your prize!</p>
      </div>
      <div class="step">
        <div class="step-number">3</div>
        <h3>Claim Your Prize</h3>
        <p>Enter your email and get your winnings instantly!</p>
      </div>
    </div>
  </section>
  
  <!-- AI-Powered Interesting Facts Section -->
  <section class="ai-facts-section" id="ai-facts">
    <h2>🤖 AI-Powered Mind-Blowing Facts</h2>
    <div class="fact-card">
      {#if isLoadingFact}
        <div class="loading-spinner">
          <div class="spinner"></div>
          <p>Generating fascinating fact...</p>
        </div>
      {:else if interestingFact?.success}
        <div class="fact-content">
          <div class="fact-icon">💡</div>
          <p class="fact-text">{interestingFact.text}</p>
          <button class="refresh-fact-btn" onclick={loadInterestingFact}>
            🔄 Generate Another Fact
          </button>
        </div>
      {:else}
        <div class="fact-content">
          <div class="fact-icon">❓</div>
          <p class="fact-text">AI features require API configuration. Set VITE_GEMINI_API_KEY to enable.</p>
        </div>
      {/if}
    </div>
  </section>
  
  <BacklinksSection />
  
  <section class="faq-section" id="faq">
    <h2>Frequently Asked Questions</h2>
    <div class="faq-grid">
      <details class="faq-item">
        <summary>Is SpinWheel Pro really free?</summary>
        <p>Yes! SpinWheel Pro is 100% free to play. You get one free spin every 24 hours. No credit card or payment information is ever required.</p>
      </details>
      <details class="faq-item">
        <summary>How do I claim my winnings?</summary>
        <p>After winning, simply enter your email address in the popup modal. We'll send you instructions on how to claim your prize within minutes!</p>
      </details>
      <details class="faq-item">
        <summary>Is this legit? Do people actually win?</summary>
        <p>Absolutely! We've paid out over $2.5 million in prizes since launch. Check our winner's wall and verified testimonials from real winners.</p>
      </details>
      <details class="faq-item">
        <summary>How often can I spin?</summary>
        <p>You get one free spin every 24 hours. The countdown resets at midnight UTC. Set a reminder so you never miss your daily chance!</p>
      </details>
      <details class="faq-item">
        <summary>What prizes can I win?</summary>
        <p>Prizes range from $10 to $1000 cash, gift cards (Amazon, Walmart, Target), electronics (iPhones, iPads, gaming consoles), and exclusive merchandise.</p>
      </details>
    </div>
  </section>
  
  <AdSenseBanner position="bottom" />
  
  <Footer />
</main>

<style>
  :global(*) {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  :global(body) {
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    min-height: 100vh;
    color: #333;
  }
  
  .app-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }
  
  .hero-section {
    text-align: center;
    padding: 60px 20px 40px;
    color: white;
  }
  
  .hero-title {
    font-size: clamp(2rem, 5vw, 3.5rem);
    font-weight: 800;
    margin-bottom: 20px;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  }
  
  .gradient-text {
    background: linear-gradient(45deg, #FFD700, #FFA500, #FF6B6B);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .hero-subtitle {
    font-size: clamp(1rem, 2.5vw, 1.3rem);
    margin-bottom: 30px;
    opacity: 0.95;
    line-height: 1.6;
  }
  
  .cta-box {
    background: rgba(255,255,255,0.15);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    padding: 30px;
    display: inline-block;
    border: 2px solid rgba(255,255,255,0.3);
  }
  
  .urgency-text {
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 20px;
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
  
  .spin-now-btn {
    background: linear-gradient(45deg, #ff416c, #ff4b2b);
    color: white;
    border: none;
    padding: 20px 50px;
    font-size: 1.5rem;
    font-weight: 700;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 10px 30px rgba(255, 75, 43, 0.4);
    text-transform: uppercase;
    letter-spacing: 2px;
  }
  
  .spin-now-btn:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 15px 40px rgba(255, 75, 43, 0.6);
  }
  
  .spin-now-btn:active {
    transform: translateY(-2px) scale(1.02);
  }
  
  .guarantee-text {
    margin-top: 15px;
    font-size: 0.9rem;
    opacity: 0.9;
  }
  
  .already-spun-box {
    background: rgba(255,255,255,0.2);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    padding: 30px;
    display: inline-block;
  }
  
  .success-message {
    font-size: 1.2rem;
    font-weight: 600;
  }
  
  .last-win {
    margin-top: 15px;
    font-size: 1.3rem;
    color: #FFD700;
  }
  
  .wheel-section {
    padding: 40px 20px;
    text-align: center;
  }
  
  .wheel-container {
    margin: 0 auto 30px;
    max-width: 500px;
  }
  
  .social-proof {
    margin-top: 30px;
  }
  
  .winner-ticker {
    background: rgba(255,255,255,0.95);
    border-radius: 10px;
    padding: 15px 25px;
    display: inline-block;
    box-shadow: 0 5px 20px rgba(0,0,0,0.2);
  }
  
  .ticker-content {
    margin-left: 10px;
    font-weight: 600;
    color: #667eea;
    animation: ticker 20s linear infinite;
    display: inline-block;
  }
  
  @keyframes ticker {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  
  .features-section {
    background: white;
    border-radius: 20px;
    padding: 60px 30px;
    margin: 40px 0;
    box-shadow: 0 10px 40px rgba(0,0,0,0.1);
  }
  
  .features-section h2 {
    text-align: center;
    font-size: 2.5rem;
    color: #667eea;
    margin-bottom: 40px;
  }
  
  .features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 30px;
  }
  
  .feature-card {
    text-align: center;
    padding: 30px 20px;
    border-radius: 15px;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    transition: transform 0.3s ease;
  }
  
  .feature-card:hover {
    transform: translateY(-10px);
  }
  
  .feature-icon {
    font-size: 3rem;
    margin-bottom: 15px;
  }
  
  .feature-card h3 {
    font-size: 1.5rem;
    color: #333;
    margin-bottom: 10px;
  }
  
  .feature-card p {
    color: #666;
    line-height: 1.6;
  }
  
  .how-it-works {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 20px;
    padding: 60px 30px;
    margin: 40px 0;
    color: white;
  }
  
  .how-it-works h2 {
    text-align: center;
    font-size: 2.5rem;
    margin-bottom: 50px;
  }
  
  .steps-container {
    display: flex;
    justify-content: space-around;
    flex-wrap: wrap;
    gap: 30px;
  }
  
  .step {
    flex: 1;
    min-width: 200px;
    text-align: center;
  }
  
  .step-number {
    width: 80px;
    height: 80px;
    background: white;
    color: #667eea;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.5rem;
    font-weight: 800;
    margin: 0 auto 20px;
    box-shadow: 0 5px 20px rgba(0,0,0,0.2);
  }
  
  .step h3 {
    font-size: 1.5rem;
    margin-bottom: 10px;
  }
  
  .step p {
    opacity: 0.9;
    line-height: 1.6;
  }
  
  .faq-section {
    background: white;
    border-radius: 20px;
    padding: 60px 30px;
    margin: 40px 0;
    box-shadow: 0 10px 40px rgba(0,0,0,0.1);
  }
  
  .faq-section h2 {
    text-align: center;
    font-size: 2.5rem;
    color: #667eea;
    margin-bottom: 40px;
  }
  
  .faq-grid {
    max-width: 800px;
    margin: 0 auto;
  }
  
  .faq-item {
    background: #f8f9fa;
    border-radius: 10px;
    margin-bottom: 15px;
    overflow: hidden;
  }
  
  .faq-item summary {
    padding: 20px;
    cursor: pointer;
    font-weight: 600;
    font-size: 1.1rem;
    list-style: none;
    position: relative;
    padding-right: 40px;
  }
  
  .faq-item summary::-webkit-details-marker {
    display: none;
  }
  
  .faq-item summary::after {
    content: '+';
    position: absolute;
    right: 20px;
    font-size: 1.5rem;
    color: #667eea;
    transition: transform 0.3s ease;
  }
  
  .faq-item[open] summary::after {
    content: '−';
    transform: rotate(180deg);
  }
  
  .faq-item p {
    padding: 0 20px 20px;
    color: #666;
    line-height: 1.8;
  }
  
  /* AI Facts Section Styles */
  .ai-facts-section {
    background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
    border-radius: 20px;
    padding: 60px 30px;
    margin: 40px 0;
    color: white;
    text-align: center;
  }
  
  .ai-facts-section h2 {
    font-size: 2.5rem;
    margin-bottom: 30px;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  }
  
  .fact-card {
    max-width: 800px;
    margin: 0 auto;
    background: rgba(255,255,255,0.15);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    padding: 40px;
    border: 2px solid rgba(255,255,255,0.3);
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .loading-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
  
  .spinner {
    width: 50px;
    height: 50px;
    border: 5px solid rgba(255,255,255,0.3);
    border-top-color: #FFD700;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  .fact-content {
    width: 100%;
  }
  
  .fact-icon {
    font-size: 4rem;
    margin-bottom: 20px;
  }
  
  .fact-text {
    font-size: 1.3rem;
    line-height: 1.8;
    margin-bottom: 25px;
    font-style: italic;
  }
  
  .refresh-fact-btn {
    background: linear-gradient(45deg, #FFD700, #FFA500);
    color: #1e3c72;
    border: none;
    padding: 15px 30px;
    font-size: 1.1rem;
    font-weight: 700;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 5px 20px rgba(255, 215, 0, 0.4);
  }
  
  .refresh-fact-btn:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 8px 30px rgba(255, 215, 0, 0.6);
  }
  
  @media (max-width: 768px) {
    .hero-section {
      padding: 40px 15px 30px;
    }
    
    .spin-now-btn {
      padding: 15px 30px;
      font-size: 1.2rem;
    }
    
    .features-section,
    .how-it-works,
    .faq-section,
    .ai-facts-section {
      padding: 40px 20px;
    }
    
    .steps-container {
      flex-direction: column;
    }
    
    .fact-card {
      padding: 25px;
    }
    
    .fact-text {
      font-size: 1.1rem;
    }
  }
</style>
